'use client';

import { useId } from 'react';
import ReactSelect, {
  type Props as ReactSelectProps,
  type StylesConfig,
  type GroupBase,
} from 'react-select';

export interface SelectOption<V = string> {
  value: V;
  label: string;
}

type AppSelectProps<V = string> = Omit<
  ReactSelectProps<SelectOption<V>, false, GroupBase<SelectOption<V>>>,
  'value' | 'onChange' | 'options'
> & {
  value: V;
  onChange: (value: V) => void;
  options: SelectOption<V>[];
  placeholder?: string;
  className?: string;
};

function buildStyles<V>(): StylesConfig<SelectOption<V>, false, GroupBase<SelectOption<V>>> {
  return {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'var(--surface)',
      borderColor: state.isFocused ? 'var(--color-primary-500)' : 'var(--border)',
      borderRadius: '0.5rem',
      boxShadow: state.isFocused ? '0 0 0 1px var(--color-primary-500)' : 'none',
      minHeight: '38px',
      fontSize: '0.875rem',
      cursor: 'pointer',
      transition: 'border-color 150ms ease, box-shadow 150ms ease',
      '&:hover': {
        borderColor: 'var(--color-primary-500)',
      },
    }),
    valueContainer: base => ({
      ...base,
      padding: '0 0.625rem',
    }),
    singleValue: base => ({
      ...base,
      color: 'var(--text)',
    }),
    placeholder: base => ({
      ...base,
      color: 'var(--muted)',
    }),
    input: base => ({
      ...base,
      color: 'var(--text)',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? 'var(--color-primary-500)' : 'var(--muted)',
      padding: '0 0.5rem',
      transition: 'color 150ms ease, transform 150ms ease',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      '&:hover': {
        color: 'var(--color-primary-500)',
      },
    }),
    menu: base => ({
      ...base,
      backgroundColor: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: '0.75rem',
      boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
      overflow: 'hidden',
      zIndex: 50,
    }),
    menuList: base => ({
      ...base,
      padding: '0.25rem',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? 'var(--color-primary-500)'
        : state.isFocused
        ? 'var(--surface)'
        : 'transparent',
      color: state.isSelected ? '#fff' : 'var(--text)',
      borderRadius: '0.5rem',
      fontSize: '0.875rem',
      cursor: 'pointer',
      padding: '0.5rem 0.75rem',
      transition: 'background-color 120ms ease',
      '&:active': {
        backgroundColor: 'var(--color-primary-500)',
        color: '#fff',
      },
    }),
    noOptionsMessage: base => ({
      ...base,
      color: 'var(--muted)',
      fontSize: '0.875rem',
    }),
  };
}

export function AppSelect<V = string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  className,
  'aria-label': ariaLabel,
  ...rest
}: AppSelectProps<V>) {
  const uid = useId();
  const selected = options.find(o => o.value === value) ?? null;

  return (
    <ReactSelect<SelectOption<V>, false, GroupBase<SelectOption<V>>>
      instanceId={uid}
      inputId={`${uid}-input`}
      // Label the hidden combobox input so it isn't reported as an unlabeled
      // form control. Falls back to the placeholder when no explicit label.
      aria-label={ariaLabel ?? placeholder}
      value={selected}
      onChange={opt => opt && onChange(opt.value)}
      options={options}
      placeholder={placeholder}
      styles={buildStyles<V>()}
      className={className}
      classNamePrefix="rs"
      isSearchable={false}
      {...rest}
    />
  );
}